import Seo from "@/components/Seo";
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, CalendarDays, ExternalLink, Search, Wifi, Users, Globe2, Ticket, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { Empty } from "@/components/Shared";
import SubmitEventModal from "@/components/SubmitEventModal";
import { useConfig } from "@/lib/useConfig";

const TYPES = ["All", "Conference", "Summit", "Hackathon", "Webinar", "Meetup", "Workshop", "Competition"];
const REGIONS = ["North America", "Europe", "Middle East", "Asia Pacific", "Africa", "Latin America", "Online Global"];
const MONTHS = [["all", "All Upcoming"], ["this", "This Month"], ["next", "Next Month"], ["3m", "Next 3 Months"], ["6m", "Next 6 Months"]];
const REACH = [["all", "All"], ["Mega", "Mega Events"], ["Large", "Large Events"], ["Community", "Community Events"]];

const REACH_STYLE = {
  Mega: "bg-[#F59E0B]/15 text-[#FBBF24] border-[#F59E0B]/40",
  Large: "bg-[#00F0FF]/12 text-[#00F0FF] border-[#00F0FF]/30",
  Medium: "bg-white/8 text-[#94A3B8] border-white/15",
  Community: "bg-white/8 text-[#94A3B8] border-white/15",
};
const TYPE_STYLE = {
  Conference: "bg-[#007AFF]/15 text-[#4da3ff] border-[#007AFF]/30",
  Summit: "bg-[#8B5CF6]/15 text-[#a78bfa] border-[#8B5CF6]/30",
  Hackathon: "bg-[#22C55E]/15 text-[#4ade80] border-[#22C55E]/30",
  Webinar: "bg-[#EC4899]/15 text-[#f472b6] border-[#EC4899]/30",
  Competition: "bg-[#F97316]/15 text-[#fb923c] border-[#F97316]/30",
  Meetup: "bg-[#14B8A6]/15 text-[#2dd4bf] border-[#14B8A6]/30",
  Workshop: "bg-[#EAB308]/15 text-[#facc15] border-[#EAB308]/30",
};

function fmtRange(s, e) {
  if (!s) return "";
  const sd = new Date(s), ed = e ? new Date(e) : null;
  const mon = sd.toLocaleDateString("en-US", { month: "short" });
  const y = sd.getFullYear();
  if (!ed || s === e) return `${mon} ${sd.getDate()} ${y}`;
  const emon = ed.toLocaleDateString("en-US", { month: "short" });
  if (mon === emon) return `${mon} ${sd.getDate()}-${ed.getDate()} ${y}`;
  return `${mon} ${sd.getDate()} – ${emon} ${ed.getDate()} ${y}`;
}

const daysUntil = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : 9999;
const isThisMonth = (d) => { if (!d) return false; const n = new Date(), s = new Date(d); return n.getMonth() === s.getMonth() && n.getFullYear() === s.getFullYear(); };

const ReachBadge = ({ level }) => (
  <span className={`font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded border ${REACH_STYLE[level] || REACH_STYLE.Medium}`}>
    {level === "Mega" ? "Mega Event" : level}
  </span>
);

function EventCard({ e, i }) {
  const soon = daysUntil(e.start_date) <= 30 && daysUntil(e.start_date) >= 0;
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.35, delay: (i % 6) * 0.04 }}
      className="card-surface rounded-xl p-5 flex flex-col" data-testid={`event-card-${e.id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className={`font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded border ${TYPE_STYLE[e.event_type] || TYPE_STYLE.Conference}`}>{e.event_type}</span>
        <ReachBadge level={e.reach_level} />
        {isThisMonth(e.start_date) && <span className="font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded bg-[#00F0FF]/15 text-[#00F0FF]">This Month</span>}
        {soon && !isThisMonth(e.start_date) && <span className="font-mono-tech text-[10px] uppercase px-2 py-0.5 rounded bg-[#F59E0B]/15 text-[#FBBF24]">Soon</span>}
      </div>
      <div className="flex items-center gap-2 text-xs text-[#94A3B8] font-mono-tech mb-1">
        <span className="text-base leading-none">{e.country_flag}</span>{e.city || e.location}{e.country && e.country !== "Global" ? `, ${e.country}` : ""}
        {e.is_online && <span className="inline-flex items-center gap-1 text-[#4ade80]"><Wifi className="w-3 h-3" /> Online</span>}
      </div>
      <h3 className="font-display font-bold text-lg leading-snug mb-1">{e.name}</h3>
      <p className="text-xs text-[#64748B] mb-3">{e.organizer}</p>
      <p className="text-sm text-[#94A3B8] flex-1 line-clamp-2">{e.description}</p>
      <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between text-xs font-mono-tech text-[#94A3B8]">
        <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#00F0FF]" /> {fmtRange(e.start_date, e.end_date)}</span>
        {e.is_free ? <span className="text-[#4ade80] font-semibold">Free</span> : <span className="text-white">{e.price}</span>}
      </div>
      {e.attendee_count ? (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] text-[#64748B] font-mono-tech"><Users className="w-3 h-3" /> {e.attendee_count.toLocaleString()} expected</div>
      ) : null}
      <a href={e.website_url || e.website} target="_blank" rel="noreferrer" data-testid={`event-link-${e.id}`}
        className="mt-4 inline-flex items-center justify-center gap-1.5 border border-white/15 hover:border-[#00F0FF]/60 text-sm px-4 py-2.5 rounded-md transition font-medium">
        Visit Official Site <ExternalLink className="w-3.5 h-3.5" />
      </a>
    </motion.div>
  );
}

const selCls = "bg-[#0e1424] border border-white/10 rounded-md px-3 py-2 text-sm text-[#e2e8f0] focus:border-[#00F0FF] focus:outline-none";

export default function Events() {
  const [all, setAll] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);
  const { paymentsEnabled } = useConfig();
  const [search, setSearch] = useState("");
  const [type, setType] = useState("All");
  const [region, setRegion] = useState("All");
  const [country, setCountry] = useState("All");
  const [month, setMonth] = useState("all");
  const [reach, setReach] = useState("all");
  const [freeOnly, setFreeOnly] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/events"), api.get("/events/stats")])
      .then(([e, s]) => { setAll(e.data); setStats(s.data); })
      .finally(() => setLoading(false));
  }, []);

  const countries = useMemo(() => {
    const set = new Set(all.filter((e) => e.country && e.country !== "Global").map((e) => e.country));
    return Array.from(set).sort();
  }, [all]);

  const filtered = useMemo(() => {
    const now = new Date();
    return all.filter((e) => {
      if (type !== "All" && e.event_type !== type) return false;
      if (region !== "All" && e.region !== region) return false;
      if (country !== "All" && e.country !== country) return false;
      if (reach !== "all" && e.reach_level !== reach) return false;
      if (freeOnly && !e.is_free) return false;
      if (search) {
        const q = search.toLowerCase();
        if (![e.name, e.city, e.location, e.organizer, e.country].some((v) => (v || "").toLowerCase().includes(q))) return false;
      }
      if (month !== "all" && e.start_date) {
        const du = daysUntil(e.start_date);
        if (month === "this" && !isThisMonth(e.start_date)) return false;
        if (month === "next") { const d = new Date(e.start_date); const nm = new Date(now.getFullYear(), now.getMonth() + 1, 1); if (d.getMonth() !== nm.getMonth() || d.getFullYear() !== nm.getFullYear()) return false; }
        if (month === "3m" && (du < 0 || du > 90)) return false;
        if (month === "6m" && (du < 0 || du > 183)) return false;
      }
      return true;
    });
  }, [all, type, region, country, month, reach, freeOnly, search]);

  const mega = useMemo(() => [...all].sort((a, b) => (b.attendee_count || 0) - (a.attendee_count || 0)).slice(0, 3), [all]);
  const byRegion = useMemo(() => {
    const m = {};
    filtered.forEach((e) => { (m[e.region] = m[e.region] || []).push(e); });
    return m;
  }, [filtered]);

  const ticker = useMemo(() => [...mega, ...filtered].slice(0, 14), [mega, filtered]);
  const tickerLoop = [...ticker, ...ticker];

  return (
    <div data-testid="events-page">
      <Seo title="AI Conferences & Events 2026 — Global Directory | WorldAIInfo" description="Find upcoming AI conferences, summits, workshops and meetups worldwide. Filter by region, type and date — updated weekly. Free directory." canonicalPath="/events" />
      {/* Ticker */}
      {ticker.length > 0 && (
        <div className="bg-[#060911] border-b border-white/10 overflow-hidden py-2.5" data-testid="events-ticker">
          <div className="ticker-track">
            {tickerLoop.map((t, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-6 text-sm text-[#94A3B8]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00F0FF]" />
                {t.name} · {fmtRange(t.start_date, t.end_date)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-14 pb-8">
        <p className="font-mono-tech text-xs uppercase tracking-widest text-[#00F0FF] mb-3">Directory</p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl">AI Events Worldwide</h1>
            <p className="mt-4 text-[#94A3B8] max-w-2xl">Discover where the AI world is gathering — conferences, hackathons, webinars and meetups.</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#94A3B8]">
            <span className="w-2 h-2 rounded-full bg-[#10B981] live-dot" /> Updated weekly
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8" data-testid="events-stats">
          {[["Upcoming Events", stats.total_upcoming], ["Countries Covered", stats.countries], ["Free Events", stats.free_events], ["This Month", stats.this_month]].map(([label, val]) => (
            <div key={label} className="card-surface rounded-xl p-4">
              <div className="font-display font-extrabold text-3xl text-[#00F0FF]">{val ?? 0}</div>
              <div className="font-mono-tech text-xs text-[#94A3B8] mt-1">{label}</div>
            </div>
          ))}
        </div>

        {/* Gold banner */}
        <div className="mt-6 rounded-xl border border-[#F59E0B]/30 bg-[#F59E0B]/[0.07] p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Ticket className="w-5 h-5 text-[#FBBF24] shrink-0" />
            <p className="text-sm text-[#fbbf24]"><span className="font-semibold">Hosting an AI event?</span> {paymentsEnabled ? "Get listed from $15." : "Get listed free while we're in pre-launch."}</p>
          </div>
          <button onClick={() => setSubmitOpen(true)} data-testid="submit-event-btn"
            className="bg-[#FBBF24] text-[#1a1204] font-semibold px-5 py-2.5 rounded-md hover:brightness-110 transition">{paymentsEnabled ? "Submit Event" : "Submit Event — Free"}</button>
        </div>
      </div>

      {/* Featured Mega */}
      {mega.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 pb-4" data-testid="featured-mega">
          <h2 className="font-display font-bold text-xl mb-4 flex items-center gap-2"><Globe2 className="w-5 h-5 text-[#FBBF24]" /> Featured Mega Events</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mega.map((e) => (
              <a key={e.id} href={e.website_url || e.website} target="_blank" rel="noreferrer" data-testid={`mega-card-${e.id}`}
                className="group rounded-2xl overflow-hidden border border-[#F59E0B]/25 bg-[#0e1424] flex flex-col hover:border-[#F59E0B]/50 transition">
                <div className="h-32 bg-gradient-to-br from-[#F59E0B]/30 via-[#00F0FF]/15 to-[#8B5CF6]/25 relative flex items-end p-4">
                  <ReachBadge level={e.reach_level} />
                  <span className="absolute top-3 right-3 text-3xl">{e.country_flag}</span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display font-bold text-lg leading-snug">{e.name}</h3>
                  <p className="text-xs text-[#94A3B8] font-mono-tech mt-1 flex items-center gap-1.5"><MapPin className="w-3 h-3 text-[#00F0FF]" /> {e.city || e.location}{e.country !== "Global" ? `, ${e.country}` : ""}</p>
                  <p className="text-sm text-[#94A3B8] mt-3 line-clamp-2 flex-1">{e.description}</p>
                  <div className="mt-4 flex items-center justify-between text-xs font-mono-tech">
                    <span className="text-[#94A3B8] flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#00F0FF]" /> {fmtRange(e.start_date, e.end_date)}</span>
                    <span className={e.is_free ? "text-[#4ade80] font-semibold" : "text-white"}>{e.is_free ? "Free" : e.price}</span>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#FBBF24] group-hover:gap-2.5 transition-all font-medium">Visit Official Site <ExternalLink className="w-3.5 h-3.5" /></span>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Filters (sticky) */}
      <div className="sticky top-16 z-30 bg-[#060911]/95 backdrop-blur border-y border-white/10 mt-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 space-y-3">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search events, cities, or organisers"
                data-testid="events-search" className="w-full bg-[#0e1424] border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm focus:border-[#00F0FF] focus:outline-none" />
            </div>
            <div className="flex flex-wrap gap-2">
              <select value={region} onChange={(e) => setRegion(e.target.value)} className={selCls} data-testid="filter-region">
                <option value="All">All Regions</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <select value={country} onChange={(e) => setCountry(e.target.value)} className={selCls} data-testid="filter-country">
                <option value="All">All Countries</option>
                {countries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={month} onChange={(e) => setMonth(e.target.value)} className={selCls} data-testid="filter-month">
                {MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <select value={reach} onChange={(e) => setReach(e.target.value)} className={selCls} data-testid="filter-reach">
                {REACH.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <button onClick={() => setFreeOnly((v) => !v)} data-testid="filter-free"
                className={`text-sm px-3 py-2 rounded-md border transition ${freeOnly ? "border-[#22C55E] text-[#4ade80] bg-[#22C55E]/10" : "border-white/10 text-[#94A3B8]"}`}>
                Free only
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <button key={t} onClick={() => setType(t)} data-testid={`event-type-${t.toLowerCase()}`}
                className={`font-mono-tech text-xs px-3 py-1.5 rounded border transition ${type === t ? "border-[#00F0FF] text-[#00F0FF] bg-[#00F0FF]/10" : "border-white/10 text-[#94A3B8] hover:border-white/25"}`}>{t}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Region sections */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {loading ? <Empty text="Loading events…" /> : filtered.length === 0 ? <Empty text="No events match your filters." /> : (
          REGIONS.filter((r) => (byRegion[r] || []).length).map((r) => {
            const list = byRegion[r];
            const open = expanded[r];
            const shown = open ? list : list.slice(0, 6);
            return (
              <section key={r} className="mb-12" data-testid={`region-section-${r.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-display font-bold text-2xl">{r} <span className="text-[#64748B] text-base font-normal">({list.length})</span></h2>
                  {list.length > 6 && (
                    <button onClick={() => setExpanded((s) => ({ ...s, [r]: !open }))} data-testid={`show-all-${r.replace(/\s+/g, "-").toLowerCase()}`}
                      className="text-sm text-[#00F0FF] hover:underline inline-flex items-center gap-1">
                      {open ? "Show less" : "Show all"} <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {shown.map((e, i) => <EventCard key={e.id} e={e} i={i} />)}
                </div>
              </section>
            );
          })
        )}

        {/* Disclaimer */}
        <div className="mt-8 rounded-xl border border-white/10 bg-white/[0.02] p-5">
          <p className="text-xs text-[#64748B] leading-relaxed" data-testid="events-disclaimer">
            Event details are sourced automatically from public platforms and verified weekly. Dates, prices and availability may change —
            always confirm with the official event organiser before booking travel or registration. WorldAIInfo.com is not responsible for any changes to listed events.
          </p>
        </div>
      </div>

      <SubmitEventModal open={submitOpen} onClose={() => setSubmitOpen(false)} />
    </div>
  );
}
